import {memo, useCallback, useMemo} from 'react'

import EntityPath from '../../../../../containers/EntityPath'
import {getFullPath} from '../../../../../selectors'
import {
  getProjectBuildTypeFilter,
  toBuildId,
  toBuildTypeId,
  toProjectId,
} from '../../../../../types'
import BuildNumberContainer from '../../../../common/BuildNumber/BuildNumber.container'
import {TreeEntityType} from '../../BuildProblems.types'
import type {AdvancedNode} from '../BuildProblemsTree.types'

import styles from './BuildProblemsTreeNode.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  dataNode: AdvancedNode
}

function BuildProblemsTreeNode({dataNode}: Props) {
  const {name, count, abstractParents = [], type} = dataNode

  const projectId = type === TreeEntityType.PROJECT ? toProjectId(name) : null
  let buildTypeId = type === TreeEntityType.BUILD_TYPE ? toBuildTypeId(name) : null
  if (type === TreeEntityType.BUILD && abstractParents.length > 0) {
    buildTypeId = toBuildTypeId(abstractParents[abstractParents.length - 1].name)
  }
  const buildId = type === TreeEntityType.BUILD ? toBuildId(name) : null

  const projectBuildTypeFilter = getProjectBuildTypeFilter({projectId, buildTypeId})
  const path = useAppSelector(state => getFullPath(state, projectBuildTypeFilter))

  const renderEntityPath = useCallback(
    (fullNodePath: Array<AdvancedNode>) => {
      const abstractParentsIds = fullNodePath.map(el => el.name)
      const filterdPath = path.filter(pathItem => abstractParentsIds.includes(pathItem.id!))
      return (
        <EntityPath
          className={styles.entityPath}
          linkClassName={styles.entityPathLink}
          projectId={projectId}
          buildTypeId={buildTypeId}
          path={filterdPath}
          projectOrBuildTypeNode={{nodeType: 'all'}}
          withLeafStatusIcon={false}
          withLeafIcon
        />
      )
    },
    [buildTypeId, path, projectId],
  )

  const renderBuild = useMemo(
    () => (
      <BuildNumberContainer
        className={styles.buildNumber}
        buildId={buildId}
        withWarnings
        hideStar
      />
    ),
    [buildId],
  )

  const renderName = useMemo(() => {
    if (type === TreeEntityType.PROJECT || type === TreeEntityType.BUILD_TYPE) {
      return renderEntityPath([...abstractParents, dataNode])
    } else if (type === TreeEntityType.BUILD && abstractParents.length > 0) {
      return (
        <>
          {renderEntityPath(abstractParents)}
          <span className={styles.separator}>{'\u00a0/\u00a0'}</span>
          {renderBuild}
        </>
      )
    } else if (type === TreeEntityType.BUILD && abstractParents.length === 0) {
      return renderBuild
    } else {
      return name ?? 'Other'
    }
  }, [abstractParents, dataNode, name, renderBuild, renderEntityPath, type])

  return (
    <div className={styles.node}>
      {renderName}
      {count != null ? <span className={styles.childrenCount}>{count}</span> : null}
    </div>
  )
}

export default memo<Props>(BuildProblemsTreeNode)
